import type { LocalizedText } from '@dob/domain';

/**
 * Talks to the Core API. The access token lives only in memory; the refresh token is an httpOnly
 * cookie the page cannot read (D87). Every call carries `x-dob-client: web`.
 *
 * - A call answered 401 is retried once after a refresh.
 * - Refreshes are serialized across browser tabs with the Web Locks API, so two tabs never rotate
 *   the same cookie at once (which the server would treat as a stolen token).
 */
export const WEB_CLIENT_HEADERS = { 'x-dob-client': 'web' } as const;

/** Codes the client produces itself (no answer from the server). */
export type ClientErrorCode = 'NETWORK' | 'BAD_RESPONSE';

export class ApiError extends Error {
  constructor(
    readonly code: string,
    readonly status: number,
    readonly serverMessage: LocalizedText | null,
    readonly requestId: string | null,
    readonly details?: unknown,
  ) {
    super(code);
    this.name = 'ApiError';
  }
}

interface ErrorBody {
  error?: { code?: string; message?: LocalizedText; requestId?: string; details?: unknown };
}

export interface TokenResponse {
  readonly accessToken: string;
  readonly accessTokenExpiresAt: string;
}

type Listener = () => void;

export interface ApiClientOptions {
  readonly baseUrl?: string;
  readonly fetch?: typeof fetch;
  readonly locks?: Pick<LockManager, 'request'> | undefined;
  readonly now?: () => number;
}

/** Refresh a little before the access token actually expires. */
const EARLY_REFRESH_MS = 30_000;

export class ApiClient {
  private accessToken: string | null = null;
  private expiresAt = 0;
  private refreshing: Promise<boolean> | null = null;
  private readonly signedOutListeners = new Set<Listener>();
  private readonly baseUrl: string;
  private readonly fetchImpl: typeof fetch;
  private readonly locks: Pick<LockManager, 'request'> | undefined;
  private readonly now: () => number;

  constructor(options: ApiClientOptions = {}) {
    this.baseUrl = options.baseUrl ?? '/api';
    this.fetchImpl = options.fetch ?? ((input, init) => fetch(input, init));
    this.locks = options.locks;
    this.now = options.now ?? Date.now;
  }

  get signedIn(): boolean {
    return this.accessToken !== null;
  }

  /** Called when the session ended (refresh refused): the app shows the sign-in screen. */
  onSignedOut(listener: Listener): () => void {
    this.signedOutListeners.add(listener);
    return () => this.signedOutListeners.delete(listener);
  }

  /** Store the tokens from a successful sign-in. */
  acceptTokens(tokens: TokenResponse): void {
    this.accessToken = tokens.accessToken;
    this.expiresAt = Date.parse(tokens.accessTokenExpiresAt);
  }

  clearTokens(): void {
    this.accessToken = null;
    this.expiresAt = 0;
  }

  /** Try to resume a sign-in from the cookie (page load). */
  async resume(): Promise<boolean> {
    return this.refresh();
  }

  async request<T>(
    method: 'GET' | 'POST' | 'PATCH' | 'DELETE',
    path: string,
    options: { body?: unknown; query?: Record<string, string | undefined> } = {},
  ): Promise<T> {
    if (this.accessToken && this.expiresAt - this.now() < EARLY_REFRESH_MS) await this.refresh();
    const hadToken = this.accessToken !== null;
    let response = await this.send(method, path, options);
    if (response.status === 401 && hadToken && (await this.refresh())) {
      response = await this.send(method, path, options);
    }
    if (response.status === 401 && hadToken && !this.accessToken) this.emitSignedOut();
    return this.parse<T>(response);
  }

  get<T>(path: string, query?: Record<string, string | undefined>): Promise<T> {
    return this.request<T>('GET', path, query ? { query } : {});
  }

  post<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>('POST', path, body === undefined ? {} : { body });
  }

  patch<T>(path: string, body: unknown): Promise<T> {
    return this.request<T>('PATCH', path, { body });
  }

  delete<T>(path: string): Promise<T> {
    return this.request<T>('DELETE', path);
  }

  /** Ends the session on the server (best effort) and forgets the tokens. */
  async logout(): Promise<void> {
    try {
      if (this.accessToken) await this.send('POST', '/auth/logout', {});
    } catch {
      // Offline: the tokens are forgotten anyway; the server session expires on its own.
    }
    this.clearTokens();
  }

  /** One refresh at a time in this tab, and across tabs when Web Locks are available. */
  refresh(): Promise<boolean> {
    this.refreshing ??= (async () => {
      try {
        const run = () => this.refreshOnce();
        return this.locks ? await this.locks.request('dob-auth-refresh', run) : await run();
      } finally {
        this.refreshing = null;
      }
    })();
    return this.refreshing;
  }

  private async refreshOnce(): Promise<boolean> {
    let response: Response;
    try {
      response = await this.fetchImpl(`${this.baseUrl}/auth/refresh`, {
        method: 'POST',
        headers: { ...WEB_CLIENT_HEADERS },
        credentials: 'same-origin',
      });
    } catch {
      // No network: keep the current token; the next call will try again.
      return false;
    }
    if (!response.ok) {
      this.clearTokens();
      return false;
    }
    this.acceptTokens((await response.json()) as TokenResponse);
    return true;
  }

  private async send(
    method: string,
    path: string,
    options: { body?: unknown; query?: Record<string, string | undefined> },
  ): Promise<Response> {
    const headers: Record<string, string> = { ...WEB_CLIENT_HEADERS };
    if (options.body !== undefined) headers['content-type'] = 'application/json';
    if (this.accessToken) headers.authorization = `Bearer ${this.accessToken}`;
    const search = new URLSearchParams();
    for (const [key, value] of Object.entries(options.query ?? {})) {
      if (value !== undefined && value !== '') search.set(key, value);
    }
    const query = search.size > 0 ? `?${search.toString()}` : '';
    try {
      return await this.fetchImpl(`${this.baseUrl}${path}${query}`, {
        method,
        headers,
        credentials: 'same-origin',
        ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
      });
    } catch {
      throw new ApiError('NETWORK', 0, null, null);
    }
  }

  private async parse<T>(response: Response): Promise<T> {
    if (response.status === 204) return undefined as T;
    let body: unknown;
    try {
      body = await response.json();
    } catch {
      throw new ApiError('BAD_RESPONSE', response.status, null, null);
    }
    if (!response.ok) {
      const error = (body as ErrorBody).error;
      throw new ApiError(
        error?.code ?? 'BAD_RESPONSE',
        response.status,
        error?.message ?? null,
        error?.requestId ?? null,
        error?.details,
      );
    }
    return body as T;
  }

  private emitSignedOut(): void {
    for (const listener of this.signedOutListeners) listener();
  }
}

export const api = new ApiClient({
  locks: typeof navigator !== 'undefined' && 'locks' in navigator ? navigator.locks : undefined,
});
